import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  ErrorOutline as ErrorIcon,
  Inventory2Outlined as MatiereIcon,
  NotesOutlined as NotesIcon,
  PaymentsOutlined as PaiementIcon,
  PersonAddAlt1Outlined as NewClientIcon,
  PersonOutline as ClientIcon,
  PhoneOutlined as PhoneIcon,
  ReceiptLongOutlined as RecapIcon,
  PrintOutlined as PrintIcon,
  WarningAmberOutlined as WarnIcon,
  BuildOutlined as TravailIcon,
  EngineeringOutlined as ChargeIcon,
  HistoryOutlined as HistoryIcon,
  AccountTreeOutlined as DetailIcon,
  CallMergeOutlined as SimplifyIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useFormDraft from '../../../hooks/useFormDraft';
import { comptoirAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  useComptoirClientOptions, useComptoirDesignations, useComptoirOptions, useComptoirUsersOptions, useInvalidateComptoir,
} from '../../../hooks/queries/useComptoir';
import ClientFormDialog from './ClientFormDialog';
import { ComptoirClientOption, ComptoirOffre, ComptoirOffreInput } from '../../../types/comptoir';
import {
  COLOR, ClientAvatar, EmptyState, NumberField, SectionCard, TotalLine,
  dh, digits, fDate, fmtPhone, num, primaryBtnSx, r2, today,
} from '../ui';

/* ================================================================ model */

type MatiereForm = {
  key: string; designation: string; type_matiere: string; quantite: string; unite: string;
  dimensions: string; date_reception: string; observations: string;
};
type TravailForm = {
  id?: number; // existing job: keeps its identity so deliveries stay linked
  key: string; type_service: string; designation: string; description: string; quantite: string;
  unite: string; prix_unitaire: string; observations: string; matiere_keys: string[];
  // Answered explicitly whenever the client brought material ('' = not answered yet)
  utilise_matiere: '' | 'oui' | 'non';
  groupe_key?: string | null; // sub-line (2.1, 2.2…) of that désignation détaillée
};
/** A désignation détaillée: a title over a run of travaux (contiguous in `travaux`), whose sum is its total. */
type GroupeForm = { key: string; libelle: string; description: string };
type FormState = {
  date: string;
  nature: '' | 'Service' | 'Projet'; // chosen at creation: row of Gestion Projets / Services
  clientId: number | null;
  chargeProjetIds: number[]; // ERP users following the offre (optional, several)
  matieres: MatiereForm[];
  groupes: GroupeForm[];
  travaux: TravailForm[];
  remise_pct: string;
  avance: string;
  date_souhaitee: string;
  observations: string;
};
type Draft = { at: number; form: FormState };

const uid = () => `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const blankTravail = (): TravailForm => ({
  key: uid(), type_service: '', designation: '', description: '', quantite: '1', unite: 'pièce',
  prix_unitaire: '', observations: '', matiere_keys: [], utilise_matiere: '',
});
const blankMatiere = (): MatiereForm => ({
  key: uid(), designation: '', type_matiere: '', quantite: '', unite: 'pièce', dimensions: '', date_reception: '', observations: '',
});
const blankForm = (): FormState => ({
  date: today(), nature: '', clientId: null, chargeProjetIds: [],
  matieres: [], groupes: [], travaux: [blankTravail()], remise_pct: '', avance: '', date_souhaitee: '', observations: '',
});
const fromOffre = (o: ComptoirOffre): FormState => ({
  date: o.date,
  nature: o.service?.nature || '',
  clientId: o.client_id,
  chargeProjetIds: (o.charges_projet || []).map((c) => c.id),
  // Existing matières keep their id as key so the links survive the round trip
  matieres: o.matieres.map((m) => ({
    key: String(m.id), designation: m.designation, type_matiere: m.type_matiere || '',
    quantite: m.quantite != null ? String(Number(m.quantite)) : '', unite: m.unite || 'pièce',
    dimensions: m.dimensions || '', date_reception: m.date_reception || '', observations: m.observations || '',
  })),
  // Existing groups keep their id as key so they are updated in place
  groupes: (o.groupes || []).map((g) => ({ key: String(g.id), libelle: g.libelle, description: g.description || '' })),
  travaux: o.travaux.map((t) => ({
    id: t.id, key: uid(), groupe_key: t.groupe_id ? String(t.groupe_id) : null, type_service: t.type_service, designation: t.designation, description: t.description || '',
    quantite: String(Number(t.quantite)), unite: t.unite, prix_unitaire: String(Number(t.prix_unitaire)),
    observations: t.observations || '', matiere_keys: t.matiere_ids.map(String),
    utilise_matiere: t.matiere_ids.length ? 'oui' : o.matieres.length ? 'non' : '',
  })),
  remise_pct: Number(o.remise_pct) ? String(Number(o.remise_pct)) : '',
  avance: Number(o.avance) ? String(Number(o.avance)) : '',
  date_souhaitee: o.date_souhaitee || '',
  observations: o.observations || '',
});

const travailEmpty = (t: TravailForm) =>
  !t.designation.trim() && !t.type_service && !t.prix_unitaire.trim() && !t.description.trim();
const matiereEmpty = (m: MatiereForm) =>
  !m.designation.trim() && !m.quantite.trim() && !m.dimensions.trim() && !m.observations.trim();

/** The désignations in order: a simple travail, or a group with its sub-lines. */
type Block = { kind: 'simple'; t: TravailForm } | { kind: 'groupe'; g: GroupeForm; lines: TravailForm[] };
const toBlocks = (travaux: TravailForm[], groupes: GroupeForm[]): Block[] => {
  const blocks: Block[] = [];
  travaux.forEach((t) => {
    const g = t.groupe_key ? groupes.find((x) => x.key === t.groupe_key) : undefined;
    const last = blocks[blocks.length - 1];
    if (!g) blocks.push({ kind: 'simple', t });
    else if (last && last.kind === 'groupe' && last.g.key === g.key) last.lines.push(t);
    else blocks.push({ kind: 'groupe', g, lines: [t] });
  });
  return blocks;
};
const lineTotalOf = (t: TravailForm) => r2(num(t.quantite) * num(t.prix_unitaire));

/** Mirrors app/services/comptoir_service.py: each line rounded, then the remise. */
const computeTotals = (f: FormState) => {
  const totalTravaux = r2(f.travaux.filter((t) => !travailEmpty(t)).reduce((a, t) => a + r2(num(t.quantite) * num(t.prix_unitaire)), 0));
  const remisePct = Math.min(100, Math.max(0, num(f.remise_pct)));
  const remise = r2(totalTravaux * remisePct / 100);
  const total = r2(totalTravaux - remise);
  const avance = r2(num(f.avance));
  return { totalTravaux, remisePct, remise, total, avance, reste: r2(total - avance) };
};
type Totals = ReturnType<typeof computeTotals>;

const STEPS = [
  { label: 'Client et matière', icon: <ClientIcon fontSize="small" /> },
  { label: 'Travaux demandés', icon: <TravailIcon fontSize="small" /> },
  { label: 'Prix et paiement', icon: <PaiementIcon fontSize="small" /> },
  { label: 'Récapitulatif', icon: <RecapIcon fontSize="small" /> },
];

/** Which step owns a field error (see `validate`). */
const stepOf = (key: string) =>
  /^(client|mat\.|date$|nature$)/.test(key) ? 0 : /^(travaux|trv\.|grp\.)/.test(key) ? 1 : 2;

/** Field-level errors, keyed by field. The server re-checks every rule. */
function validate(f: FormState, t: Totals, paid = 0): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.date) e.date = 'Indiquez la date de la demande.';
  if (!f.nature) e.nature = 'Choisissez Service ou Projet.';
  if (!f.clientId) e.client = 'Choisissez le client, ou créez-en un nouveau.';
  f.matieres.forEach((m) => {
    if (matiereEmpty(m)) return;
    if (!m.designation.trim()) e[`mat.${m.key}.designation`] = 'Indiquez ce que le client a apporté (ou supprimez la ligne).';
    if (m.date_reception && m.date_reception > today()) e[`mat.${m.key}.date_reception`] = 'La date de réception ne peut pas être dans le futur.';
  });

  const live = f.travaux.filter((x) => !travailEmpty(x));
  const mats = f.matieres.filter((m) => m.designation.trim());
  if (!live.length) e.travaux = 'Ajoutez au moins un travail.';
  live.forEach((x) => {
    if (!x.type_service) e[`trv.${x.key}.type_service`] = 'Choisissez le type.';
    if (!x.designation.trim()) e[`trv.${x.key}.designation`] = 'Décrivez le travail en quelques mots.';
    if (!(num(x.quantite) > 0)) e[`trv.${x.key}.quantite`] = 'Supérieure à 0.';
    if (x.prix_unitaire.trim() === '') e[`trv.${x.key}.prix_unitaire`] = 'Obligatoire (0 si compris ailleurs).';
    if (mats.length) {
      if (!x.utilise_matiere) e[`trv.${x.key}.utilise_matiere`] = 'Répondez Oui ou Non.';
      else if (x.utilise_matiere === 'oui' && !x.matiere_keys.some((k) => mats.some((m) => m.key === k)))
        e[`trv.${x.key}.utilise_matiere`] = 'Cochez la matière utilisée par ce travail.';
    }
  });

  f.groupes.forEach((g) => {
    if (!f.travaux.some((x) => x.groupe_key === g.key)) return;
    if (!g.libelle.trim()) e[`grp.${g.key}.libelle`] = 'Donnez un nom à la désignation.';
    else if (!live.some((x) => x.groupe_key === g.key)) e[`grp.${g.key}.libelle`] = 'Ajoutez au moins un travail à cette désignation.';
  });

  if (num(f.remise_pct) > 100) e.remise_pct = 'La remise ne peut pas dépasser 100 %.';
  if (!e.travaux && t.total <= 0) e.total = 'Le total général doit être supérieur à 0.';
  if (t.avance > t.total) e.avance = `L'avance dépasse le total (${dh(t.total)}).`;
  if (paid > 0 && t.total < paid) e.total = `Le total ne peut pas être inférieur au montant déjà encaissé (${dh(paid)}).`;
  if (f.date_souhaitee && f.date && f.date_souhaitee < f.date) e.date_souhaitee = 'Doit être après la date de la demande.';
  return e;
}

/* ================================================================ component */

interface OffreWizardProps {
  open: boolean;
  offre: ComptoirOffre | null; // null = new offre
  presetClientId?: number | null; // new offre started from a client's page
  onClose: () => void;
  onSaved: (o: ComptoirOffre, isNew: boolean, print: boolean) => void;
}

type ConfirmState = {
  title: string; message: string; confirmText: string; cancelText: string;
  type?: 'warning' | 'info' | 'error';
  onConfirm: () => void; onCancel?: () => void;
} | null;

const OffreWizard: React.FC<OffreWizardProps> = ({ open, offre, presetClientId, onClose, onSaved }) => {
  const isEdit = !!offre;
  const { data: options } = useComptoirOptions();
  const { data: clients = [] } = useComptoirClientOptions(open);
  const invalidateComptoir = useInvalidateComptoir();
  const { data: users = [] } = useComptoirUsersOptions(open);
  const { data: designations = [] } = useComptoirDesignations(open);
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<Draft>('comptoir-offre', !isEdit);

  const [step, setStep] = useState(0);
  const [f, setF] = useState<FormState>(blankForm());
  const [reference, setReference] = useState('');
  const [shown, setShown] = useState<Set<number>>(new Set()); // steps whose errors are visible
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientInit, setNewClientInit] = useState<{ nom?: string; telephone?: string }>({});
  const [createdClient, setCreatedClient] = useState<ComptoirClientOption | null>(null);
  const [motif, setMotif] = useState(''); // edit only: why the jobs change, kept in the history
  const [motifShown, setMotifShown] = useState(false);
  const confirmed = useRef(false);
  const initial = useRef('');

  /* ---------- open / reset ---------- */
  useEffect(() => {
    if (!open) return;
    setServerError('');
    setShown(new Set());
    setClientSearch('');
    setMotif('');
    setMotifShown(false);
    if (offre) {
      const form = fromOffre(offre);
      setF(form);
      initial.current = JSON.stringify(form);
      setReference(offre.reference);
      setStep(3); // an existing offre opens on its recap
      return;
    }
    const form = { ...blankForm(), clientId: presetClientId ?? null };
    setF(form);
    initial.current = JSON.stringify(form);
    setStep(0);
    comptoirAPI.getNextReference().then((r) => setReference(r.reference)).catch(() => setReference(''));
    // Started from a client's page: a fresh demande for that client, the saved draft stays for later
    const draft = presetClientId ? null : loadDraft();
    if (draft && draft.form) {
      const n = draft.form.travaux.filter((t) => !travailEmpty(t)).length;
      askConfirm({
        type: 'info',
        title: 'Reprendre la saisie ?',
        message: `Une demande non terminée du ${new Date(draft.at).toLocaleString('fr-FR')} — ${n} travail(x) — a été gardée. Voulez-vous la reprendre ?`,
        confirmText: 'Reprendre',
        cancelText: 'Nouvelle saisie',
        onConfirm: () => {
          const form = { ...draft.form, groupes: draft.form.groupes || [] }; // drafts saved before the désignations
          setF(form);
          initial.current = JSON.stringify(form);
        },
        onCancel: () => clearDraft(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offre]);

  const askConfirm = (c: NonNullable<ConfirmState>) => { confirmed.current = false; setConfirm(c); };

  /* ---------- derived ---------- */
  const totals = useMemo(() => computeTotals(f), [f]);
  // In edit, the avance is what the service has already collected: read-only here
  const paid = isEdit ? r2(num(offre!.avance)) : 0;
  const errors = useMemo(() => {
    const e = validate(f, totals, paid);
    if (paid > 0) delete e.avance;
    return e;
  }, [f, totals, paid]);
  const stepErrors = (s: number) => Object.keys(errors).filter((k) => stepOf(k) === s);
  const err = (key: string) => (shown.has(stepOf(key)) ? errors[key] : undefined);
  const dirty = JSON.stringify(f) !== initial.current;
  const selectedClient = clients.find((c) => c.id === f.clientId) || (createdClient?.id === f.clientId ? createdClient : null);
  const liveMatieres = f.matieres.filter((m) => m.designation.trim());
  const liveTravaux = f.travaux.filter((t) => !travailEmpty(t));
  const blocks = toBlocks(f.travaux, f.groupes);

  /* ---------- updates ---------- */
  const set = (patch: Partial<FormState>) => setF((p) => ({ ...p, ...patch }));
  const setTravail = (key: string, patch: Partial<TravailForm>) =>
    setF((p) => ({ ...p, travaux: p.travaux.map((t) => (t.key === key ? { ...t, ...patch } : t)) }));
  const setMatiere = (key: string, patch: Partial<MatiereForm>) =>
    setF((p) => ({ ...p, matieres: p.matieres.map((m) => (m.key === key ? { ...m, ...patch } : m)) }));

  const removeMatiere = (m: MatiereForm) => {
    const run = () => setF((p) => ({
      ...p,
      matieres: p.matieres.filter((x) => x.key !== m.key),
      travaux: p.travaux.map((t) => ({ ...t, matiere_keys: t.matiere_keys.filter((k) => k !== m.key) })),
    }));
    if (matiereEmpty(m)) return run();
    askConfirm({
      type: 'warning', title: 'Supprimer cette matière ?',
      message: `« ${m.designation || 'Matière sans nom'} » sera retirée de la demande et des travaux qui l'utilisent.`,
      confirmText: 'Supprimer', cancelText: 'Annuler', onConfirm: run,
    });
  };
  const removeTravail = (t: TravailForm, label: string) => {
    const g = t.groupe_key ? f.groupes.find((x) => x.key === t.groupe_key) : undefined;
    if (g && f.travaux.filter((x) => x.groupe_key === g.key).length === 1) {
      // Last sub-line: the désignation becomes simple again, named by its libellé
      setF((p) => ({
        ...p,
        groupes: p.groupes.filter((x) => x.key !== g.key),
        travaux: p.travaux.map((x) => (x.key === t.key ? { ...x, groupe_key: null, designation: g.libelle || x.designation } : x)),
      }));
      return;
    }
    const run = () => setF((p) => ({ ...p, travaux: p.travaux.length > 1 ? p.travaux.filter((x) => x.key !== t.key) : [blankTravail()] }));
    if (travailEmpty(t)) return run();
    askConfirm({
      type: 'warning', title: `Supprimer le travail ${label} ?`,
      message: `« ${t.designation || t.type_service || 'Travail'} » (${dh(lineTotalOf(t))}) sera retiré de la demande.`,
      confirmText: 'Supprimer', cancelText: 'Annuler', onConfirm: run,
    });
  };
  /** « Détailler »: the travail's designation becomes the libellé, the travail its first sub-line. */
  const detailTravail = (t: TravailForm) => {
    const g: GroupeForm = { key: uid(), libelle: t.designation, description: '' };
    setF((p) => ({
      ...p,
      groupes: [...p.groupes, g],
      travaux: p.travaux.map((x) => (x.key === t.key ? { ...x, groupe_key: g.key, designation: '' } : x)),
    }));
  };
  const setGroupe = (key: string, patch: Partial<GroupeForm>) =>
    setF((p) => ({ ...p, groupes: p.groupes.map((g) => (g.key === key ? { ...g, ...patch } : g)) }));
  const addSubLine = (gkey: string) => setF((p) => {
    const at = p.travaux.map((x) => x.groupe_key).lastIndexOf(gkey) + 1;
    return { ...p, travaux: [...p.travaux.slice(0, at), { ...blankTravail(), groupe_key: gkey }, ...p.travaux.slice(at)] };
  });
  const removeGroupe = (g: GroupeForm, n: number) => {
    const lines = f.travaux.filter((x) => x.groupe_key === g.key);
    const run = () => setF((p) => {
      const rest = p.travaux.filter((x) => x.groupe_key !== g.key);
      return { ...p, groupes: p.groupes.filter((x) => x.key !== g.key), travaux: rest.length ? rest : [blankTravail()] };
    });
    if (!g.libelle.trim() && lines.every(travailEmpty)) return run();
    askConfirm({
      type: 'warning', title: `Supprimer la désignation ${n} ?`,
      message: `« ${g.libelle || 'Désignation'} » et ses ${lines.length} travail(x) (${dh(r2(lines.reduce((a, x) => a + lineTotalOf(x), 0)))}) seront retirés de la demande.`,
      confirmText: 'Supprimer', cancelText: 'Annuler', onConfirm: run,
    });
  };

  /* ---------- navigation ---------- */
  const focusFirstError = (s: number) => {
    const first = stepErrors(s)[0];
    if (!first) return;
    window.setTimeout(() => {
      const el = document.getElementById(`f-${first}`);
      if (el) {
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input, textarea, [role="combobox"]') as HTMLElement | null;
        (input || el).focus?.();
      }
    }, 80);
  };
  const goTo = (n: number) => {
    setServerError('');
    if (n > step) {
      for (let s = step; s < n; s++) {
        if (stepErrors(s).length) {
          setShown((p) => new Set(p).add(s));
          setStep(s);
          focusFirstError(s);
          return;
        }
      }
    }
    setStep(n);
    const content = document.getElementById('comptoir-wizard-content');
    if (content) content.scrollTop = 0;
  };

  /* ---------- save ---------- */
  const handleSave = async (print = false) => {
    for (const s of [0, 1, 2]) {
      if (stepErrors(s).length) {
        setShown((p) => new Set(p).add(s));
        setStep(s);
        focusFirstError(s);
        return;
      }
    }
    if (isEdit && !motif.trim()) {
      setMotifShown(true);
      setStep(3);
      window.setTimeout(() => (document.querySelector('#f-motif input') as HTMLElement | null)?.focus(), 80);
      return;
    }
    const keep = new Set(liveMatieres.map((m) => m.key));
    const payload: ComptoirOffreInput = {
      date: f.date,
      client_id: f.clientId,
      charge_projet_ids: f.chargeProjetIds || [],
      remise_pct: r2(num(f.remise_pct)),
      avance: totals.avance,
      date_souhaitee: f.date_souhaitee || null,
      observations: f.observations.trim() || null,
      motif: isEdit ? motif.trim() : null,
      nature: isEdit ? null : (f.nature || null),
      matieres: liveMatieres.map((m) => ({
        key: m.key, designation: m.designation.trim(), type_matiere: m.type_matiere || null,
        quantite: m.quantite.trim() === '' ? null : num(m.quantite), unite: m.unite || null,
        dimensions: m.dimensions.trim() || null, date_reception: m.date_reception || null,
        observations: m.observations.trim() || null,
      })),
      // Only groups that still have a travail; their order follows the travaux
      groupes: f.groupes
        .filter((g) => liveTravaux.some((t) => t.groupe_key === g.key))
        .map((g) => ({ key: g.key, libelle: g.libelle.trim(), description: g.description.trim() || null })),
      travaux: liveTravaux.map((t) => ({
        groupe_key: t.groupe_key || null,
        type_service: t.type_service, designation: t.designation.trim(), description: t.description.trim() || null,
        quantite: num(t.quantite), unite: t.unite, prix_unitaire: num(t.prix_unitaire),
        observations: t.observations.trim() || null, id: t.id,
        matiere_keys: t.utilise_matiere === 'oui' ? t.matiere_keys.filter((k) => keep.has(k)) : [],
      })),
    };
    setSaving(true);
    setServerError('');
    try {
      const saved = isEdit ? await comptoirAPI.updateOffre(offre!.id, payload) : await comptoirAPI.createOffre(payload);
      if (!presetClientId) clearDraft(); // a demande started from a client's page never used the draft
      onSaved(saved, !isEdit, print);
    } catch (e: any) {
      setServerError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- closing ---------- */
  const requestClose = () => {
    if (saving) return;
    if (!dirty) { if (!isEdit && !presetClientId) clearDraft(); onClose(); return; }
    askConfirm({
      type: 'warning',
      title: isEdit ? 'Fermer sans enregistrer ?' : 'Fermer la demande ?',
      message: isEdit
        ? 'Les modifications non enregistrées seront perdues.'
        : 'Votre saisie sera gardée en brouillon : elle vous sera proposée à la prochaine « Nouvelle demande ».',
      confirmText: 'Fermer',
      cancelText: 'Continuer la saisie',
      onConfirm: () => { if (!isEdit) saveDraft({ at: Date.now(), form: f }); onClose(); },
    });
  };

  /* ================================================================ render helpers */
  const matTypeLabel = (v: string) => options?.types_matiere.find((x) => x.value === v)?.label || '—';
  const stepSummary = (s: number): string => {
    if (s === 0) {
      const name = selectedClient?.nom;
      return name ? `${name}${liveMatieres.length ? ` · ${liveMatieres.length} matière(s)` : ''}` : 'Client à choisir';
    }
    if (s === 1) return liveTravaux.length ? `${liveTravaux.length} travail(x) · ${dh(totals.totalTravaux)}` : 'Aucun travail';
    if (s === 2) return totals.total > 0 ? `Total ${dh(totals.total)}` : 'Remise et avance';
    return 'Vérifier et enregistrer';
  };

  const stepNav = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
      {STEPS.map((s, i) => {
        const active = i === step;
        const hasErr = shown.has(i) && stepErrors(i).length > 0;
        const done = i < step && !stepErrors(i).length;
        return (
          <ButtonBase
            key={s.label}
            onClick={() => goTo(i)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, textAlign: 'left', justifyContent: 'flex-start',
              border: '1.5px solid', borderColor: hasErr ? 'error.main' : active ? COLOR : 'divider',
              bgcolor: active ? alpha(COLOR, 0.07) : 'transparent', transition: 'all .2s',
              '&:hover': { borderColor: hasErr ? 'error.main' : COLOR },
            }}
          >
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 14,
              bgcolor: hasErr ? 'error.main' : done ? 'success.main' : active ? COLOR : 'action.selected',
              color: hasErr || done || active ? '#fff' : 'text.secondary',
            }}>
              {hasErr ? <ErrorIcon fontSize="small" /> : done ? <CheckIcon fontSize="small" /> : i + 1}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: active ? 700 : 600 }} noWrap>{s.label}</Typography>
              <Typography variant="caption" color={hasErr ? 'error' : 'text.secondary'} noWrap display="block">
                {hasErr ? `${stepErrors(i).length} point(s) à corriger` : stepSummary(i)}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );

  /* ================================================================ step 1 */
  const openNewClient = (typed = '') => {
    const t = typed.trim();
    // What was typed in the search pre-fills the popup: a number goes to the phone, text to the name
    setNewClientInit(digits(t).length >= 6 && !/[a-z]/i.test(t) ? { telephone: t } : { nom: t });
    setNewClientOpen(true);
  };

  const clientSection = (
    <SectionCard
      id="f-client"
      icon={<ClientIcon />}
      title="Client"
      subtitle="Retrouvez-le par son nom, son surnom ou son téléphone"
      error={!!err('client')}
      action={
        <Button size="small" variant="outlined" startIcon={<NewClientIcon />} onClick={() => openNewClient(clientSearch)}
          sx={{ borderColor: alpha(COLOR, 0.5), color: COLOR }}>
          Nouveau client
        </Button>
      }
    >
      {selectedClient ? (
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 2, bgcolor: alpha(COLOR, 0.04), borderColor: alpha(COLOR, 0.3) }}>
          <ClientAvatar name={selectedClient.nom} size={48} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {selectedClient.nom}
              {selectedClient.surnom && <Typography component="span" color="text.secondary"> « {selectedClient.surnom} »</Typography>}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
              <PhoneIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fmtPhone(selectedClient.telephone)}</Typography>
            </Stack>
          </Box>
          {paid > 0
            ? <Tooltip title="Des paiements sont enregistrés pour ce client"><span><Button size="small" startIcon={<EditIcon />} disabled>Changer</Button></span></Tooltip>
            : <Button size="small" startIcon={<EditIcon />} onClick={() => { setClientSearch(''); set({ clientId: null }); }}>Changer</Button>}
        </Paper>
      ) : (
        <Autocomplete
          options={clients}
          value={null}
          inputValue={clientSearch}
          onInputChange={(_, v, reason) => { if (reason !== 'reset') setClientSearch(v); }}
          onChange={(_, v) => v && set({ clientId: v.id })}
          getOptionLabel={(c) => c.nom}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText={
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="body2">Aucun client trouvé{clientSearch.trim() ? ` pour « ${clientSearch.trim()} »` : ''}.</Typography>
              <Button size="small" startIcon={<NewClientIcon />} sx={{ color: COLOR }}
                onMouseDown={(e) => { e.preventDefault(); openNewClient(clientSearch); }}>
                {clientSearch.trim() ? `Créer « ${clientSearch.trim()} »` : 'Créer un nouveau client'}
              </Button>
            </Stack>
          }
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            const qd = digits(inputValue);
            if (!q) return opts.slice(0, 50);
            return opts.filter((c) =>
              c.nom.toLowerCase().includes(q) || (c.surnom || '').toLowerCase().includes(q) ||
              (qd.length >= 3 && digits(c.telephone).includes(qd))).slice(0, 50);
          }}
          renderOption={(props, c) => (
            <li {...props} key={c.id}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                <ClientAvatar name={c.nom} size={32} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {c.nom}{c.surnom && <Typography component="span" variant="body2" color="text.secondary"> « {c.surnom} »</Typography>}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone)}</Typography>
                </Box>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Rechercher un client"
              placeholder="Nom, surnom ou téléphone…"
              error={!!err('client')}
              helperText={err('client') || `${clients.length} client(s) enregistré(s) · pas dans la liste ? « Nouveau client »`}
            />
          )}
        />
      )}
    </SectionCard>
  );

  const matiereSection = (
    <SectionCard
      icon={<MatiereIcon />}
      title="Matière fournie par le client"
      subtitle="Tôles, tubes ou pièces que le client apporte — gardez la trace de ce qu'il a remis"
      action={f.matieres.length > 0 && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => set({ matieres: [...f.matieres, blankMatiere()] })} sx={{ color: COLOR }}>
          Ajouter
        </Button>
      )}
    >
      {f.matieres.length === 0 ? (
        <EmptyState
          compact
          icon={<MatiereIcon />}
          title="Aucune matière du client"
          text="Si le client n'apporte rien, passez à l'étape suivante. Pour facturer une matière fournie par l'atelier, ajoutez un travail « Fourniture de matière »."
          action={{ label: 'Ajouter une matière ou une pièce', icon: <AddIcon />, onClick: () => set({ matieres: [blankMatiere()] }) }}
        />
      ) : (
        <Stack spacing={1.5}>
          {f.matieres.map((m, i) => (
            <Paper key={m.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }} color="text.secondary">Matière {i + 1}</Typography>
                <Tooltip title="Supprimer cette matière">
                  <IconButton size="small" onClick={() => removeMatiere(m)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} id={`f-mat.${m.key}.designation`}>
                  <TextField
                    label="Désignation" required fullWidth size="small" placeholder="ex : Tôle inox, portail à réparer"
                    value={m.designation} onChange={(e) => setMatiere(m.key, { designation: e.target.value })}
                    error={!!err(`mat.${m.key}.designation`)} helperText={err(`mat.${m.key}.designation`)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField select label="Type" fullWidth size="small" value={m.type_matiere} onChange={(e) => setMatiere(m.key, { type_matiere: e.target.value })}>
                    <MenuItem value=""><em>Non précisé</em></MenuItem>
                    {(options?.types_matiere || []).map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={7} sm={3} md={2}>
                  <NumberField label="Quantité reçue" fullWidth size="small" value={m.quantite} onChange={(v) => setMatiere(m.key, { quantite: v })} />
                </Grid>
                <Grid item xs={5} sm={3} md={1.5}>
                  <TextField select label="Unité" fullWidth size="small" value={m.unite} onChange={(e) => setMatiere(m.key, { unite: e.target.value })}>
                    {(options?.unites || []).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField label="Dimensions / épaisseur" fullWidth size="small" placeholder="ex : 2000 × 1000 × 2 mm"
                    value={m.dimensions} onChange={(e) => setMatiere(m.key, { dimensions: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} id={`f-mat.${m.key}.date_reception`}>
                  <TextField
                    label="Date de réception" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                    inputProps={{ max: today() }}
                    value={m.date_reception} onChange={(e) => setMatiere(m.key, { date_reception: e.target.value })}
                    error={!!err(`mat.${m.key}.date_reception`)}
                    helperText={err(`mat.${m.key}.date_reception`) || (m.date_reception ? 'Déposée' : 'Vide = pas encore déposée')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Observations" fullWidth size="small" placeholder="État, chutes à rendre…"
                    value={m.observations} onChange={(e) => setMatiere(m.key, { observations: e.target.value })} />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );

  const step1 = (
    <>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="N° d'offre commerciale" fullWidth value={reference || '…'}
            InputProps={{ readOnly: true }}
            helperText={isEdit ? 'Numéro définitif' : "Réservé à l'enregistrement, jamais réutilisé"}
            sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, color: COLOR } }}
          />
        </Grid>
        <Grid item xs={12} sm={4} id="f-date">
          <TextField
            label="Date de la demande" type="date" fullWidth required InputLabelProps={{ shrink: true }}
            value={f.date} onChange={(e) => set({ date: e.target.value })}
            error={!!err('date')} helperText={err('date') || ' '}
          />
        </Grid>
        <Grid item xs={12} sm={4} id="f-nature">
          <ToggleButtonGroup
            exclusive fullWidth value={f.nature || null} disabled={isEdit}
            onChange={(_, v) => v && set({ nature: v })}
            aria-label="Service ou Projet"
            sx={{
              height: 56,
              '& .MuiToggleButton-root': { borderColor: err('nature') ? 'error.main' : undefined, fontWeight: 600 },
              '& .Mui-selected': { bgcolor: `${alpha(COLOR, 0.12)} !important`, color: `${COLOR} !important` },
            }}
          >
            <ToggleButton value="Service">Service</ToggleButton>
            <ToggleButton value="Projet">Projet</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, ml: 1.75, color: err('nature') ? 'error.main' : 'text.secondary' }}>
            {err('nature') || (isEdit
              ? 'Pour changer le type : « Modifier » sur le service'
              : f.nature === 'Projet' ? 'Numéro PRJ26-… dans Gestion Projets / Services'
                : f.nature === 'Service' ? 'Numéro SRV26-… dans Gestion Projets / Services' : 'Type de la demande * : Service ou Projet ?')}
          </Typography>
        </Grid>
      </Grid>
      {clientSection}
      <SectionCard
        icon={<ChargeIcon />}
        title="Chargé(s) de projet"
        subtitle="Facultatif — les personnes qui suivent cette demande à l'atelier"
      >
        <Autocomplete
          multiple
          options={users}
          value={users.filter((u) => (f.chargeProjetIds || []).includes(u.id))}
          onChange={(_, v) => set({ chargeProjetIds: v.map((u) => u.id) })}
          getOptionLabel={(u) => u.full_name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterSelectedOptions
          noOptionsText="Aucun utilisateur"
          renderOption={(props, u) => (
            <li {...props} key={u.id}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ClientAvatar name={u.full_name} size={28} />
                <Typography variant="body2">{u.full_name}</Typography>
              </Stack>
            </li>
          )}
          renderTags={(value, getTagProps) => value.map((u, i) => (
            <Chip {...getTagProps({ index: i })} key={u.id} size="small" label={u.full_name}
              sx={{ bgcolor: alpha(COLOR, 0.12), color: COLOR, fontWeight: 600, '& .MuiChip-deleteIcon': { color: alpha(COLOR, 0.6) } }} />
          ))}
          renderInput={(params) => (
            <TextField {...params} label="Choisir un ou plusieurs utilisateurs"
              placeholder={(f.chargeProjetIds || []).length ? '' : 'Rechercher un utilisateur…'} />
          )}
        />
      </SectionCard>
      {matiereSection}
    </>
  );

  /* ================================================================ step 2 */
  /** One travail: a simple désignation, or a sub-line (label 2.1, 2.2…) of a détaillée. */
  const travailCard = (t: TravailForm, label: string, sub = false, lastSub = false) => {
    const lineTotal = lineTotalOf(t);
    const e = (k: keyof TravailForm) => err(`trv.${t.key}.${k}`);
    const hasErr = ['type_service', 'designation', 'quantite', 'prix_unitaire', 'utilise_matiere'].some((k) => e(k as keyof TravailForm));
    return (
      <Paper key={t.key} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: hasErr ? 'error.main' : 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, bgcolor: alpha(COLOR, 0.05), borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ minWidth: 28, height: 28, px: sub ? 0.75 : 0, borderRadius: 14, bgcolor: sub ? alpha(COLOR, 0.15) : COLOR, color: sub ? COLOR : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
            {label}
          </Box>
          <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
            {t.designation.trim() || <Typography component="span" variant="body2" color="text.secondary">Nouveau travail</Typography>}
            {t.type_service && <Chip label={t.type_service} size="small" sx={{ ml: 1, height: 22 }} />}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700, color: lineTotal > 0 ? COLOR : 'text.disabled' }}>
            {dh(lineTotal)}
          </Typography>
          {!sub && (
            <Tooltip title="Détailler en plusieurs travaux (2.1, 2.2…)">
              <Button
                size="small" variant="outlined" startIcon={<DetailIcon />} onClick={() => detailTravail(t)}
                sx={{
                  color: COLOR, borderColor: alpha(COLOR, 0.5), bgcolor: alpha(COLOR, 0.08), fontWeight: 600, whiteSpace: 'nowrap',
                  '&:hover': { borderColor: COLOR, bgcolor: alpha(COLOR, 0.16) },
                }}
              >
                Détailler
              </Button>
            </Tooltip>
          )}
          {lastSub ? (
            <Tooltip title="Retirer le détail : redevient une désignation simple">
              <IconButton size="small" onClick={() => removeTravail(t, label)}><SimplifyIcon fontSize="small" /></IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={sub || f.travaux.length > 1 ? 'Supprimer ce travail' : 'Vider ce travail'}>
              <IconButton size="small" onClick={() => removeTravail(t, label)}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Stack>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} id={`f-trv.${t.key}.type_service`}>
              <TextField
                select label="Type de service" required fullWidth size="small" value={t.type_service}
                onChange={(ev) => setTravail(t.key, { type_service: ev.target.value })}
                error={!!e('type_service')} helperText={e('type_service')}
              >
                {(options?.types_service || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8} id={`f-trv.${t.key}.designation`}>
              <Autocomplete
                freeSolo
                options={designations}
                inputValue={t.designation}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') setTravail(t.key, { designation: v }); }}
                onChange={(_, v) => {
                  if (!v || typeof v === 'string') return;
                  // A suggestion brings its last type, price and unit — only into fields still empty
                  setTravail(t.key, {
                    designation: v.designation,
                    ...(!t.type_service && v.type_service ? { type_service: v.type_service } : {}),
                    ...(!t.prix_unitaire.trim() && v.prix_unitaire != null ? { prix_unitaire: String(Number(v.prix_unitaire)) } : {}),
                    ...(t.unite === 'pièce' && v.unite ? { unite: v.unite } : {}),
                  });
                }}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.designation)}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  const hits = q ? opts.filter((o) => o.designation.toLowerCase().includes(q)) : opts;
                  // Same type of service first, then the most used
                  return hits.slice().sort((a, b) =>
                    Number(b.type_service === t.type_service) - Number(a.type_service === t.type_service)).slice(0, 30);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.designation}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{o.designation}</Typography>
                        {o.count > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {[o.type_service, o.prix_unitaire != null ? `dernier prix ${dh(Number(o.prix_unitaire))}` : null, `${o.count} fois`].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      {o.count > 0 && <HistoryIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={sub ? 'Description du travail' : 'Désignation'} required size="small"
                    placeholder={sub ? 'ex : Découpe des tôles' : "ex : Fabrication d'un support métallique"}
                    error={!!e('designation')}
                    helperText={e('designation') || (designations.some((d) => d.count > 0) ? 'Tapez pour retrouver un travail déjà saisi' : undefined)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth size="small" multiline minRows={2} placeholder="Détails, cotes, finition…"
                value={t.description} onChange={(ev) => setTravail(t.key, { description: ev.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2} id={`f-trv.${t.key}.quantite`}>
              <NumberField label="Quantité" required fullWidth size="small" value={t.quantite}
                onChange={(v) => setTravail(t.key, { quantite: v })} error={!!e('quantite')} helperText={e('quantite')} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select label="Unité" fullWidth size="small" value={t.unite} onChange={(ev) => setTravail(t.key, { unite: ev.target.value })}>
                {(options?.unites || []).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3} id={`f-trv.${t.key}.prix_unitaire`}>
              <NumberField label="Prix unitaire" required fullWidth size="small" unit="DH" value={t.prix_unitaire}
                onChange={(v) => setTravail(t.key, { prix_unitaire: v })} error={!!e('prix_unitaire')} helperText={e('prix_unitaire')} />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField label="Observations" fullWidth size="small" value={t.observations}
                onChange={(ev) => setTravail(t.key, { observations: ev.target.value })} />
            </Grid>
            {liveMatieres.length > 0 && (
              <Grid item xs={12} id={`f-trv.${t.key}.utilise_matiere`}>
                <Box sx={{
                  p: 1.5, borderRadius: 2, border: '1px solid',
                  borderColor: e('utilise_matiere') ? 'error.main' : alpha(COLOR, 0.3),
                  bgcolor: alpha(COLOR, 0.04),
                }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={{ xs: 0.5, sm: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <MatiereIcon sx={{ fontSize: 18, color: COLOR }} />
                      Ce travail utilise-t-il la matière du client ?
                      <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>
                    <RadioGroup
                      row
                      value={t.utilise_matiere || ''}
                      onChange={(ev) => {
                        const v = ev.target.value as 'oui' | 'non';
                        setTravail(t.key, {
                          utilise_matiere: v,
                          // Only one material: nothing to choose, tick it
                          matiere_keys: v === 'non' ? [] : liveMatieres.length === 1 ? [liveMatieres[0].key] : t.matiere_keys,
                        });
                      }}
                    >
                      <FormControlLabel value="oui" control={<Radio size="small" sx={{ '&.Mui-checked': { color: COLOR } }} />} label="Oui" />
                      <FormControlLabel value="non" control={<Radio size="small" sx={{ '&.Mui-checked': { color: COLOR } }} />} label="Non" />
                    </RadioGroup>
                  </Stack>
                  <Collapse in={t.utilise_matiere === 'oui'}>
                    <Stack sx={{ mt: 0.5, pl: { sm: 3.25 } }}>
                      {liveMatieres.map((m) => {
                        const on = t.matiere_keys.includes(m.key);
                        return (
                          <FormControlLabel
                            key={m.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={on}
                                sx={{ '&.Mui-checked': { color: COLOR } }}
                                onChange={() => setTravail(t.key, { matiere_keys: on ? t.matiere_keys.filter((k) => k !== m.key) : [...t.matiere_keys, m.key] })}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {m.designation}
                                {m.quantite && <Typography component="span" variant="body2" color="text.secondary"> · {m.quantite} {m.unite}</Typography>}
                                {m.dimensions && <Typography component="span" variant="body2" color="text.secondary"> · {m.dimensions}</Typography>}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Stack>
                  </Collapse>
                  {e('utilise_matiere') && <FormHelperText error sx={{ mt: 0.5 }}>{e('utilise_matiere')}</FormHelperText>}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    );
  };

  /** A désignation détaillée: libellé, description, its sub-lines and their total. */
  const groupeCard = (g: GroupeForm, lines: TravailForm[], n: number) => {
    const err = shown.has(1) ? errors[`grp.${g.key}.libelle`] : undefined;
    const total = r2(lines.reduce((a, t) => a + lineTotalOf(t), 0));
    return (
      <Paper key={g.key} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: err ? 'error.main' : alpha(COLOR, 0.4), bgcolor: alpha(COLOR, 0.02) }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ mt: 0.5, width: 28, height: 28, flexShrink: 0, borderRadius: '50%', bgcolor: COLOR, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
            {n}
          </Box>
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }} id={`f-grp.${g.key}.libelle`}>
            <TextField
              label={`Désignation ${n}`} required fullWidth size="small" value={g.libelle}
              placeholder="ex : Structure métallique"
              onChange={(ev) => setGroupe(g.key, { libelle: ev.target.value })}
              error={!!err} helperText={err}
            />
            <TextField
              label="Description générale (facultatif)" fullWidth size="small" multiline value={g.description}
              onChange={(ev) => setGroupe(g.key, { description: ev.target.value })}
            />
          </Stack>
          <Box sx={{ textAlign: 'right', pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">Total de la désignation</Typography>
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700, color: total > 0 ? COLOR : 'text.disabled' }}>{dh(total)}</Typography>
          </Box>
          <Tooltip title="Supprimer la désignation et ses travaux">
            <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => removeGroupe(g, n)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
        <Stack spacing={1.5} sx={{ px: 2, pb: 1.5, pl: { sm: 6 } }}>
          {lines.map((t, j) => travailCard(t, `${n}.${j + 1}`, true, lines.length === 1))}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addSubLine(g.key)} sx={{ color: COLOR, borderColor: alpha(COLOR, 0.5) }}>
              Ajouter un travail
            </Button>
            <Typography variant="body2" color="text.secondary">
              Total de la désignation {n} : <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>{dh(total)}</Box>
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const step2 = (
    <>
      {errors.travaux && shown.has(1) && <Alert severity="error" sx={{ mb: 2 }} id="f-travaux">{errors.travaux}</Alert>}
      <Stack spacing={2}>
        {blocks.map((b, i) => (b.kind === 'simple' ? travailCard(b.t, String(i + 1)) : groupeCard(b.g, b.lines, i + 1)))}
      </Stack>
      <Button
        fullWidth variant="outlined" startIcon={<AddIcon />}
        onClick={() => set({ travaux: [...f.travaux, blankTravail()] })}
        sx={{ mt: 2, py: 1.5, borderStyle: 'dashed', borderColor: alpha(COLOR, 0.5), color: COLOR, '&:hover': { borderStyle: 'dashed', borderColor: COLOR } }}
      >
        Ajouter une désignation
      </Button>
      <Alert severity="info" icon={<TravailIcon />} sx={{ mt: 2 }}>
        Le type de service décrit ce que demande le client. Les étapes de fabrication (découpe, pliage, soudage…) se programment ensuite.
      </Alert>
    </>
  );

  /* ================================================================ step 3 */
  const avancePct = totals.total > 0 ? Math.min(100, Math.round(totals.avance / totals.total * 100)) : 0;
  const totalsCard = (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, position: { md: 'sticky' }, top: 0 }}>
      <Typography variant="overline" color="text.secondary">Montant de l'offre</Typography>
      <TotalLine label="Total des travaux" value={dh(totals.totalTravaux)} />
      {totals.remisePct > 0 && <TotalLine label={`Remise ${totals.remisePct} %`} value={`− ${dh(totals.remise)}`} color="success.main" />}
      <Divider sx={{ my: 1 }} />
      <TotalLine label="Total général" value={dh(totals.total)} strong />
      <TotalLine label={isEdit ? 'Déjà encaissé' : 'Avance reçue'} value={totals.avance ? dh(totals.avance) : '—'} />
      <Box sx={{ my: 1 }}>
        <LinearProgress variant="determinate" value={avancePct}
          sx={{ height: 8, borderRadius: 4, bgcolor: alpha(COLOR, 0.12), '& .MuiLinearProgress-bar': { bgcolor: totals.reste < 0 ? 'error.main' : avancePct >= 100 ? 'success.main' : COLOR } }} />
        <Typography variant="caption" color="text.secondary">{avancePct} % payé</Typography>
      </Box>
      {/* Owed (> 0) or impossible (< 0, avance above the total): both are shown as a problem */}
      <TotalLine label="Reste à payer" value={dh(totals.reste)} strong color={totals.reste === 0 ? 'success.main' : 'error.main'} />
    </Paper>
  );

  const step3 = (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <SectionCard icon={<PaiementIcon />} title="Prix et paiement" subtitle="La remise s'applique au total des travaux">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5} id="f-remise_pct">
              <NumberField label="Remise" fullWidth unit="%" value={f.remise_pct} onChange={(v) => set({ remise_pct: v })}
                error={!!err('remise_pct')} helperText={err('remise_pct') || (totals.remise ? `− ${dh(totals.remise)}` : 'Facultative')} />
            </Grid>
            <Grid item xs={12} sm={7} id="f-avance">
              {isEdit ? (
                <NumberField label="Déjà encaissé" fullWidth unit="DH" value={paid ? String(paid) : '0'} onChange={() => undefined}
                  InputProps={{ readOnly: true }}
                  helperText={"Les paiements s'enregistrent depuis le service (« Encaisser »)."} />
              ) : (<>
              <NumberField label="Avance donnée par le client" fullWidth unit="DH" value={f.avance} onChange={(v) => set({ avance: v })}
                error={!!err('avance')}
                helperText={err('avance') || (totals.avance ? `${avancePct} % du total · bon de paiement émis en espèces` : 'Vide si le client n\'a rien payé')} />
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {[0, 30, 50, 100].map((p) => (
                  <Chip key={p} size="small" variant="outlined" clickable
                    label={p === 0 ? 'Aucune' : `${p} %`}
                    disabled={totals.total <= 0}
                    onClick={() => set({ avance: p === 0 ? '' : r2(totals.total * p / 100).toFixed(2) })} />
                ))}
              </Stack>
              </>)}
            </Grid>
            <Grid item xs={12} sm={6} id="f-date_souhaitee">
              <TextField label="Date souhaitée par le client" type="date" fullWidth InputLabelProps={{ shrink: true }}
                inputProps={{ min: f.date }} value={f.date_souhaitee} onChange={(e) => set({ date_souhaitee: e.target.value })}
                error={!!err('date_souhaitee')} helperText={err('date_souhaitee') || 'Facultative'} />
            </Grid>
          </Grid>
          {err('total') && <Alert severity="error" sx={{ mt: 2 }} id="f-total">{err('total')}</Alert>}
        </SectionCard>
        <SectionCard icon={<NotesIcon />} title="Observations" subtitle="Conditions, délai, précisions pour l'atelier…">
          <TextField fullWidth multiline minRows={3} placeholder="Facultatif" value={f.observations} onChange={(e) => set({ observations: e.target.value })} />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={5}>{totalsCard}</Grid>
    </Grid>
  );

  /* ================================================================ step 4 */
  const warnings = useMemo(() => {
    const out: string[] = [];
    liveMatieres.forEach((m) => {
      if (!liveTravaux.some((t) => t.utilise_matiere === 'oui' && t.matiere_keys.includes(m.key)))
        out.push(`La matière « ${m.designation} » apportée par le client n'est utilisée par aucun travail.`);
      if (!m.date_reception) out.push(`« ${m.designation} » n'est pas encore déposée par le client.`);
    });
    if (!isEdit && totals.avance === 0 && totals.total > 0) out.push("Aucune avance n'a été versée.");
    return out;
  }, [liveMatieres, liveTravaux, totals, isEdit]);

  const recapClient = {
    nom: selectedClient?.nom || offre?.client_nom || '—',
    surnom: selectedClient?.surnom || '',
    tel: selectedClient?.telephone || offre?.client_telephone || '',
  };

  const recapHead = (title: string, back: number) => (
    <Stack direction="row" alignItems="center" sx={{ mt: 3, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
      <Button size="small" startIcon={<EditIcon />} onClick={() => goTo(back)} sx={{ color: COLOR }}>Modifier</Button>
    </Stack>
  );

  const motifErr = motifShown && !motif.trim() ? "Indiquez le motif : il est gardé dans l'historique du service." : '';
  const step4 = (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        {isEdit && (
          <TextField id="f-motif" label="Motif de la modification" required fullWidth sx={{ mb: 2 }}
            placeholder="ex : le client ajoute 2 pièces, prix revu…" value={motif}
            onChange={(e) => setMotif(e.target.value)} error={!!motifErr}
            helperText={motifErr || 'Si les travaux, la matière ou la remise changent, la version actuelle est gardée et reste imprimable.'} />
        )}
        {warnings.length > 0 && (
          <Alert severity="warning" icon={<WarnIcon />} sx={{ mb: 2 }}>
            {warnings.length === 1 ? warnings[0] : <Box component="ul" sx={{ m: 0, pl: 2 }}>{warnings.map((w) => <li key={w}>{w}</li>)}</Box>}
          </Alert>
        )}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <ClientAvatar name={recapClient.nom} size={52} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {recapClient.nom}{recapClient.surnom && <Typography component="span" color="text.secondary"> « {recapClient.surnom} »</Typography>}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fmtPhone(recapClient.tel)}</Typography>
            {(f.chargeProjetIds || []).length > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                <ChargeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                {users.filter((u) => f.chargeProjetIds.includes(u.id)).map((u) => (
                  <Chip key={u.id} label={u.full_name} size="small" sx={{ height: 20, fontSize: 11 }} />
                ))}
              </Stack>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Offre {reference}{f.nature ? ` · ${f.nature}` : ''}
            </Typography>
            <Typography variant="body2">Le {fDate(f.date)}</Typography>
            {f.date_souhaitee && <Typography variant="caption" color="text.secondary">Souhaitée le {fDate(f.date_souhaitee)}</Typography>}
          </Box>
          <IconButton size="small" onClick={() => goTo(0)}><EditIcon fontSize="small" /></IconButton>
        </Paper>

        {recapHead(`Travaux demandés (${liveTravaux.length})`, 1)}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(COLOR, 0.05) }}>
                <TableCell>#</TableCell><TableCell>Travail</TableCell>
                <TableCell align="right">Qté</TableCell><TableCell align="right">P.U.</TableCell><TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {toBlocks(liveTravaux, f.groupes).flatMap((blk, i) => {
                const row = (t: TravailForm, label: string, sub: boolean) => {
                  const used = t.utilise_matiere === 'oui' ? liveMatieres.filter((m) => t.matiere_keys.includes(m.key)).map((m) => m.designation) : [];
                  return (
                    <TableRow key={t.key}>
                      <TableCell sx={{ pl: sub ? 4 : undefined, whiteSpace: 'nowrap' }}>{label}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.designation}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          <Chip label={t.type_service} size="small" sx={{ height: 20, fontSize: 11 }} />
                          {used.map((u) => <Chip key={u} icon={<MatiereIcon />} label={u} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />)}
                        </Stack>
                        {t.description && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{t.description}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{num(t.quantite)} {t.unite}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dh(num(t.prix_unitaire))}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{dh(lineTotalOf(t))}</TableCell>
                    </TableRow>
                  );
                };
                if (blk.kind === 'simple') return [row(blk.t, String(i + 1), false)];
                // Désignation détaillée: a header row with its total, then its lines n.1, n.2…
                return [
                  <TableRow key={blk.g.key} sx={{ bgcolor: alpha(COLOR, 0.03) }}>
                    <TableCell sx={{ fontWeight: 700 }}>{i + 1}</TableCell>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{blk.g.libelle}</Typography>
                      {blk.g.description.trim() && <Typography variant="caption" color="text.secondary" display="block">{blk.g.description}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {dh(r2(blk.lines.reduce((acc, t) => acc + lineTotalOf(t), 0)))}
                    </TableCell>
                  </TableRow>,
                  ...blk.lines.map((t, j) => row(t, `${i + 1}.${j + 1}`, true)),
                ];
              })}
            </TableBody>
          </Table>
        </Paper>

        {recapHead(`Matière fournie par le client (${liveMatieres.length})`, 0)}
        {liveMatieres.length ? (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(COLOR, 0.05) }}>
                  <TableCell>Matière</TableCell><TableCell>Type</TableCell><TableCell align="right">Quantité</TableCell>
                  <TableCell>Dimensions</TableCell><TableCell>Réception</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liveMatieres.map((m) => (
                  <TableRow key={m.key}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.designation}</Typography>
                      {m.observations && <Typography variant="caption" color="text.secondary">{m.observations}</Typography>}
                    </TableCell>
                    <TableCell>{matTypeLabel(m.type_matiere)}</TableCell>
                    <TableCell align="right">{m.quantite ? `${m.quantite} ${m.unite}` : '—'}</TableCell>
                    <TableCell>{m.dimensions || '—'}</TableCell>
                    <TableCell>
                      {m.date_reception
                        ? <Chip label={`Reçue le ${fDate(m.date_reception)}`} size="small" color="success" variant="outlined" />
                        : <Chip label="À déposer" size="small" color="warning" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ) : <Typography variant="body2" color="text.secondary">Le client n'a apporté aucune matière.</Typography>}

        {f.observations.trim() && (
          <>
            {recapHead('Observations', 2)}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, whiteSpace: 'pre-wrap' }}>
              <Typography variant="body2">{f.observations}</Typography>
            </Paper>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>{totalsCard}</Grid>
    </Grid>
  );

  /* ================================================================ dialog */
  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose /* backdrop / Escape: asks before losing unsaved input */}
        maxWidth="lg"
        fullWidth
        aria-labelledby="comptoir-wizard-title"
        PaperProps={{ sx: { borderRadius: 3, height: { md: '92vh' } } }}
      >
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(COLOR, 0.12), color: COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RecapIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography id="comptoir-wizard-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {isEdit ? 'Offre commerciale' : 'Nouvelle demande'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{reference || '…'}</Box>
              {isEdit ? ` · client ${offre!.client_nom || ''}` : ' · offre commerciale acceptée par le client'}
            </Typography>
          </Box>
          <Tooltip title="Fermer">
            <IconButton onClick={requestClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Box>

        <DialogContent id="comptoir-wizard-content" sx={{ px: 3, py: 2.5 }}>
          {stepNav}
          <Collapse in={!!serverError}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError('')}>{serverError}</Alert>
          </Collapse>
          {[step1, step2, step3, step4][step]}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', gap: 1 }}>
          <Button onClick={requestClose} color="inherit" disabled={saving}>Annuler</Button>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Total <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>{dh(totals.total)}</Box>
            </Typography>
            {totals.avance > 0 && (
              <Typography variant="body2" color="text.secondary">
                Reste <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: totals.reste === 0 ? 'success.main' : 'error.main' }}>{dh(totals.reste)}</Box>
              </Typography>
            )}
          </Box>
          {step > 0 && <Button variant="outlined" startIcon={<BackIcon />} onClick={() => goTo(step - 1)} disabled={saving}>Précédent</Button>}
          {step < 3 ? (
            <Button variant="contained" endIcon={<NextIcon />} onClick={() => goTo(step + 1)} sx={primaryBtnSx}>Suivant</Button>
          ) : (
            <>
              <Button
                variant="outlined" onClick={() => handleSave(false)} disabled={saving}
                startIcon={<CheckIcon />} sx={{ borderColor: COLOR, color: COLOR }}
              >
                {isEdit ? 'Enregistrer' : "Créer l'offre"}
              </Button>
              <Button
                variant="contained" onClick={() => handleSave(true)} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 200 }}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
              >
                {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer + devis' : 'Créer + imprimer le devis'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ClientFormDialog
        open={newClientOpen}
        initial={newClientInit}
        onClose={() => setNewClientOpen(false)}
        save={(data) => comptoirAPI.createClientFromOffre(data)}
        findDuplicate={(tel) => clients.find((c) => digits(c.telephone) === digits(tel)) || null}
        onUseExisting={(id) => { setNewClientOpen(false); setClientSearch(''); set({ clientId: id }); }}
        onSaved={(c) => {
          setNewClientOpen(false);
          setCreatedClient(c);
          setClientSearch('');
          set({ clientId: c.id });
          invalidateComptoir(); // the new client shows up in the picker and on the Clients page
        }}
      />

      <AlertDialog
        open={!!confirm}
        type={confirm?.type || 'warning'}
        title={confirm?.title}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText}
        cancelText={confirm?.cancelText}
        showCancel
        onConfirm={() => { confirmed.current = true; confirm?.onConfirm(); }}
        onClose={() => {
          // AlertDialog calls onClose after onConfirm too: only a real cancel runs onCancel
          if (!confirmed.current) confirm?.onCancel?.();
          setConfirm(null);
        }}
      />
    </>
  );
};

export default OffreWizard;
